import type { ReactNode } from "react";
import { formatDateTime, formatRelative } from "@/lib/format";
import "./engagement.css";

/**
 * Shared engagement-panel primitives — the presentational layer both wrappers compose:
 * ReportEngagement (lead drawer, api 326) and QuoteEngagement (quote editor, api 335).
 *
 * Everything here is deliberately dumb: the wrappers own fetching, headline logic and any
 * subject-specific tags/counters, because that is where report and quote genuinely differ.
 * What they share — time formatting, the dwell bars, the session rows, the 0-means-never
 * convention — lives here exactly once.
 */

/** "4m 12s" / "38s" — dwell is the headline number in the session list, so it reads as time. */
export function dwell(ms: number): string {
  const total = Math.round((ms || 0) / 1000);
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
}

/** Epoch ms, or 0 for "never" — both engagement schemas use 0 rather than null throughout. */
export function stamped(value: number | undefined | null): boolean {
  return typeof value === "number" && value > 0;
}

/** `formatRelative` guards null/undefined/"" but NOT 0, and 0 is these schemas' "never" — passed
 *  straight through it renders "01 Jan 1970". Every timestamp in a panel goes through this. */
export function ago(value: number | undefined | null): string {
  return stamped(value) ? formatRelative(value) : "—";
}

/** The structural shape SectionDwell needs — both APIs emit exactly this. */
export interface SectionTotalLike {
  section_key: string;
  section_state: "full" | "empty" | string;
  dwell_ms: number;
  sessions_reached?: number;
}

export function SectionDwell({
  totals,
  labelFor,
}: {
  totals: SectionTotalLike[];
  /** Subject-specific naming: prettyLabel for report sections, the block-type map for quotes. */
  labelFor: (key: string) => string;
}) {
  // Scale to the longest bar rather than to the total: the question is which sections held them,
  // and relative depth answers it. An absolute scale would flatten every bar on a long read.
  const peak = Math.max(...totals.map((t) => t.dwell_ms), 1);
  return (
    <div className="reng__sections">
      {totals.map((t) => {
        const empty = t.section_state === "empty";
        return (
          <div key={t.section_key} className="reng__section">
            <span className="reng__section-name">{labelFor(t.section_key)}</span>
            <span className="reng__track">
              <span
                className={`reng__bar${empty ? " reng__bar--empty" : ""}`}
                style={{ width: `${Math.max(2, (t.dwell_ms / peak) * 100)}%` }}
              />
            </span>
            <span className="reng__section-val">
              {dwell(t.dwell_ms)}
              {/* NOT "empty" — that word is wrong and it misleads. The section still renders real
                  client-facing copy. What `section_state` records is that the no-items BRANCH
                  rendered rather than a list. The bar is still greyed, because a short section
                  banks most of its dwell from whatever is being read above it. */}
              {empty ? (
                <em title="This section had nothing to list, so it rendered its standard message instead. Short sections accrue dwell from their neighbours, so this time is not evidence of interest in the topic.">
                  {" "}
                  · none to list
                </em>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* These deliberately don't add up, and the gap is wide enough to look like a bug: a 42s read
   can measure 71s across its sections. Dwell accrues for EVERY section at least a quarter on
   screen, so neighbours accrue at the same time. Rendered by both wrappers under the chart. */
export function SectionOverlapFootnote() {
  return (
    <p className="reng__note reng__note--foot">
      Sections overlap — dwell counts every section at least a quarter on screen, so these
      won’t add up to the time on page. Compare the bars, not the totals.
    </p>
  );
}

/** The structural shape SessionRow needs; the optional meta fields render only when present. */
export interface SessionLike {
  id: number;
  started_at: number;
  total_ms: number;
  max_scroll_pct: number;
  device_type?: string;
  ua_family?: string;
  country?: string;
}

export function SessionRow({ s, tags }: { s: SessionLike; tags?: ReactNode }) {
  return (
    <div className="reng__session">
      <div>
        <strong>{formatDateTime(s.started_at)}</strong>
        <span className="reng__meta">
          {[s.device_type, s.ua_family, s.country].filter(Boolean).join(" · ")}
        </span>
      </div>
      <div className="reng__session-stats">
        <span title="Time on the page, with the tab visible">{dwell(s.total_ms)}</span>
        <span title="How far down the page they scrolled">{s.max_scroll_pct}%</span>
        {tags}
      </div>
    </div>
  );
}
